/**
 * GaliaScene — el paisaje desbloqueable detrás de Galia.
 *
 * Renderiza un fondo tipo rompecabezas ("la casa de Galia") que se arma pieza
 * por pieza según el progreso de gamificación del estudiante. La paleta cambia
 * por nivel MCER. Cada pieza es un `<g data-piece="...">` que un diseñador puede
 * reemplazar por la ilustración final.
 *
 * Se coloca como capa de fondo dentro de `.app-container` (z-index 0), sin
 * capturar clics (pointer-events: none). Solo para estudiantes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import gamificationService from '../../services/gamificationService';
import achievementService from '../../services/achievementService';
import {
  SCENE_PIECES,
  ProgressStats,
  ScenePalette,
  paletteForLevel,
  revealedPieceIds,
  nextLockedPiece,
  IllustratedScene,
  illustratedSceneForLevel,
  trophyIdForIllustratedPiece,
} from '../../config/galiaScenes';
import { ACHIEVEMENTS_BY_ID } from '../../config/actionAchievements';
import { TROPHIES_BY_ID } from '../../config/trophies';
import { MCERLevel } from '../../types';
import './GaliaScene.css';

/* ─────────────────────────────────────────────────────────────
   Preview solo-dev (nunca en prod). Permite inspeccionar el arte y
   las paletas por URL sin depender del progreso real ni de un email:
     ?scene_preview=all         → revela todas las piezas
     ?scene_preview=none        → escena vacía
     ?scene_preview=5           → revela las primeras 5 piezas
     ?scene_palette=B2          → fuerza la paleta de ese nivel MCER
   Gateado igual que el modo testing de la app (VITE_TESTING_PHASE).
   ───────────────────────────────────────────────────────────── */

function isPreviewAllowed(): boolean {
  const env = import.meta.env.VITE_TESTING_PHASE;
  if (env !== undefined) return env === 'true' || (env as unknown) === true;
  return import.meta.env.DEV === true;
}

interface ScenePreview {
  /** Cuántas piezas revelar (null = todas). */
  count: number | null;
  palette?: MCERLevel;
}

function readScenePreview(): ScenePreview | null {
  if (typeof window === 'undefined' || !isPreviewAllowed()) return null;
  const params = new URLSearchParams(window.location.search);
  const preview = params.get('scene_preview');
  const paletteParam = params.get('scene_palette');
  const validPalette =
    paletteParam && ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].includes(paletteParam)
      ? (paletteParam as MCERLevel)
      : undefined;
  if (!preview && !validPalette) return null;

  let count: number | null = null; // 'all' o solo override de paleta → todo
  if (preview === 'none') count = 0;
  else if (preview && /^\d+$/.test(preview)) count = Math.max(0, parseInt(preview, 10));
  return { count, palette: validPalette };
}

interface GaliaSceneProps {
  email?: string | null;
  mcerLevel?: MCERLevel;
  /** Cuando el chat está abierto, atenuamos la escena para no competir. */
  chatVisible?: boolean;
}

const EMPTY_STATS: ProgressStats = { level: 0, streak: 0, interactions: 0 };

export function GaliaScene({ email, mcerLevel, chatVisible = false }: GaliaSceneProps) {
  const [stats, setStats] = useState<ProgressStats>(EMPTY_STATS);
  const [achievements, setAchievements] = useState<Set<string>>(new Set());
  const [justUnlocked, setJustUnlocked] = useState<Set<string>>(new Set());
  const prevRevealed = useRef<Set<string>>(new Set());
  const initialized = useRef(false);

  // Preview solo-dev por URL (?scene_preview / ?scene_palette). Estable por montaje.
  const preview = useMemo(readScenePreview, []);

  useEffect(() => {
    if (preview) return; // en preview no cargamos datos reales
    if (!email) return;
    let cancelled = false;

    const load = async () => {
      try {
        const [level, streak, interactions, ach] = await Promise.all([
          gamificationService.getCurrentLevel(email),
          gamificationService.getCurrentStreak(email),
          gamificationService.getTotalInteractions(email),
          achievementService.getUnlockedAchievements(email),
        ]);
        if (cancelled) return;

        const next: ProgressStats = { level, streak, interactions };
        const revealedNow = revealedPieceIds(next, ach, mcerLevel);

        // Detectar piezas nuevas para animar el "pop" celebratorio.
        // En la primera carga no celebramos (mostramos el estado ya alcanzado).
        if (initialized.current) {
          const fresh = new Set<string>();
          revealedNow.forEach((id) => {
            if (!prevRevealed.current.has(id)) fresh.add(id);
          });
          if (fresh.size > 0) {
            setJustUnlocked(fresh);
            window.setTimeout(() => setJustUnlocked(new Set()), 2200);
          }
        }

        prevRevealed.current = revealedNow;
        initialized.current = true;
        setStats(next);
        setAchievements(ach);
      } catch (err) {
        console.error('[GaliaScene] Error al cargar progreso:', err);
      }
    };

    load();

    // Refrescar cuando la gamificación reporta un cambio (interacción / conexión).
    const onProgress = () => load();
    window.addEventListener('galia:progress-updated', onProgress);
    return () => {
      cancelled = true;
      window.removeEventListener('galia:progress-updated', onProgress);
    };
  }, [email, preview]);

  const sceneLevel = preview?.palette ?? mcerLevel;
  const illustrated = illustratedSceneForLevel(sceneLevel);
  if (illustrated && sceneLevel) {
    return (
      <IllustratedSceneView
        scene={illustrated}
        level={sceneLevel}
        achievements={achievements}
        ready={!!preview || initialized.current}
        previewCount={preview ? preview.count : undefined}
        chatVisible={chatVisible}
      />
    );
  }

  const palette = paletteForLevel(sceneLevel);
  const revealed = preview
    ? new Set(SCENE_PIECES.slice(0, preview.count ?? SCENE_PIECES.length).map((p) => p.id))
    : revealedPieceIds(stats, achievements, mcerLevel);
  const next = preview ? null : nextLockedPiece(stats, achievements);
  // Pista de la acción que desbloquea la siguiente pieza (hint del trofeo).
  const nextHint = next?.achievement ? ACHIEVEMENTS_BY_ID[next.achievement]?.hint : undefined;
  const total = SCENE_PIECES.length;
  const done = revealed.size;

  return (
    <div
      className={`galia-scene ${chatVisible ? 'chat-visible' : ''}`}
      aria-hidden="true"
    >
      <SceneSvg palette={palette} revealed={revealed} justUnlocked={justUnlocked} />

      {/* Píldora de progreso — discreta, motiva a completar la casa de Galia. */}
      {done > 0 && (
        <div className="galia-scene-progress">
          <span className="galia-scene-progress-count">{done}/{total}</span>
          <span className="galia-scene-progress-label">
            {done >= total
              ? 'La casa de Galia ✨'
              : next
                ? `Descubre ${next.label}${nextHint ? ` — ${nextHint}` : ''}`
                : 'Sigue practicando'}
          </span>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Escena ilustrada (arte final de la AF): fondo + 7 piezas, una por
   trofeo de bronce del nivel; con las 7 se funde a fondo_completo.
   ───────────────────────────────────────────────────────────── */

interface IllustratedSceneViewProps {
  scene: IllustratedScene;
  level: MCERLevel;
  achievements: Set<string>;
  /** false hasta la primera carga del progreso (no celebrar lo ya ganado). */
  ready: boolean;
  /** Preview solo-dev: nº de piezas a revelar (null = todas). */
  previewCount?: number | null;
  chatVisible: boolean;
}

function IllustratedSceneView({
  scene,
  level,
  achievements,
  ready,
  previewCount,
  chatVisible,
}: IllustratedSceneViewProps) {
  // Orden de revelado = orden de los trofeos del nivel (para preview y píldora).
  const byTrophy = useMemo(
    () => [...scene.pieces].sort((a, b) => a.trophy - b.trophy),
    [scene]
  );
  const revealed = useMemo(() => {
    if (previewCount !== undefined) {
      return new Set(byTrophy.slice(0, previewCount ?? byTrophy.length).map((p) => p.id));
    }
    const ids = new Set<string>();
    for (const p of scene.pieces) {
      const trophyId = trophyIdForIllustratedPiece(p, level);
      if (trophyId && achievements.has(trophyId)) ids.add(p.id);
    }
    return ids;
  }, [scene, level, achievements, previewCount, byTrophy]);

  // "Pop" de las piezas recién ganadas (no en la primera carga).
  const [justUnlocked, setJustUnlocked] = useState<Set<string>>(new Set());
  const prev = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!ready) return;
    if (prev.current) {
      const fresh = new Set([...revealed].filter((id) => !prev.current!.has(id)));
      if (fresh.size > 0) {
        setJustUnlocked(fresh);
        const t = window.setTimeout(() => setJustUnlocked(new Set()), 2200);
        prev.current = revealed;
        return () => window.clearTimeout(t);
      }
    }
    prev.current = revealed;
  }, [revealed, ready]);

  const total = scene.pieces.length;
  const done = revealed.size;
  const complete = done >= total;
  const next = byTrophy.find((p) => !revealed.has(p.id));
  const nextTrophy = next ? trophyIdForIllustratedPiece(next, level) : null;
  const nextHint = nextTrophy ? TROPHIES_BY_ID[nextTrophy]?.hint : undefined;
  const src = (name: string) => `/assets/scenes/${scene.dir}/${name}.webp`;
  const cls = (id: string) =>
    `scene-piece ${revealed.has(id) ? 'revealed' : 'locked'} ${
      justUnlocked.has(id) ? 'just-unlocked' : ''
    }`;

  return (
    <div
      className={`galia-scene galia-scene--illustrated ${chatVisible ? 'chat-visible' : ''}`}
      aria-hidden="true"
    >
      <svg
        className="galia-scene-svg"
        viewBox={`0 0 ${scene.width} ${scene.height}`}
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <image href={src('fondo')} x="0" y="0" width={scene.width} height={scene.height} />
        {scene.pieces.map((p) => (
          <image
            key={p.id}
            data-piece={p.id}
            className={cls(p.id)}
            href={src(p.id)}
            x={p.x}
            y={p.y}
            width={p.w}
            height={p.h}
            preserveAspectRatio="none"
          />
        ))}
        {/* Con los 7 trofeos, el fondo completo (versión pulida del diseño). */}
        <image
          className={`galia-scene-complete ${complete ? 'revealed' : ''}`}
          href={complete ? src('fondo_completo') : undefined}
          x="0"
          y="0"
          width={scene.width}
          height={scene.height}
        />
      </svg>

      <div className="galia-scene-progress">
        <span className="galia-scene-progress-count">{done}/{total}</span>
        <span className="galia-scene-progress-label">
          {complete
            ? `${scene.place} ✨`
            : next
              ? `Descubre ${next.label}${nextHint ? ` — ${nextHint}` : ''}`
              : 'Sigue practicando'}
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Arte SVG (placeholder). viewBox 800×600, "slice" para cubrir.
   Cada pieza es un <g data-piece> con clase revealed/locked.
   ───────────────────────────────────────────────────────────── */

interface SceneSvgProps {
  palette: ScenePalette;
  revealed: Set<string>;
  justUnlocked: Set<string>;
}

function SceneSvg({ palette: p, revealed, justUnlocked }: SceneSvgProps) {
  const cls = (id: string) =>
    `scene-piece ${revealed.has(id) ? 'revealed' : 'locked'} ${
      justUnlocked.has(id) ? 'just-unlocked' : ''
    }`;

  return (
    <svg
      className="galia-scene-svg"
      viewBox="0 0 800 600"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="galia-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={p.skyTop} />
          <stop offset="100%" stopColor={p.skyBottom} />
        </linearGradient>
        <radialGradient id="galia-sun-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={p.celestialGlow} stopOpacity="0.9" />
          <stop offset="100%" stopColor={p.celestialGlow} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Base (siempre): cielo — "el lienzo en blanco" del comienzo */}
      <rect x="0" y="0" width="800" height="600" fill="url(#galia-sky)" />

      {/* Estrellas (solo noche/B2), parte del cielo base */}
      {p.night && (
        <g opacity="0.85">
          {STARS.map((s, i) => (
            <circle key={i} cx={s[0]} cy={s[1]} r={s[2]} fill="#ffffff" opacity={s[3]} />
          ))}
        </g>
      )}

      {/* Suelo base sutil para asentar las piezas */}
      <rect x="0" y="470" width="800" height="130" fill={p.ground} opacity="0.55" />

      {/* ── Pieza: sol / luna ── */}
      <g data-piece="sun" className={cls('sun')}>
        <circle cx="640" cy="140" r="120" fill="url(#galia-sun-glow)" />
        <circle cx="640" cy="140" r="52" fill={p.celestial} />
        {p.night && <circle cx="620" cy="128" r="44" fill={p.skyTop} opacity="0.55" />}
      </g>

      {/* ── Pieza: colinas ── */}
      <g data-piece="hills" className={cls('hills')}>
        <path d="M0 480 Q 200 380 420 470 T 800 440 L 800 600 L 0 600 Z" fill={p.hillsFar} />
        <path d="M0 520 Q 260 430 520 510 T 800 500 L 800 600 L 0 600 Z" fill={p.hillsNear} />
      </g>

      {/* ── Pieza: árbol (el ejemplo de Santiago: "el arbolito") ── */}
      <g data-piece="tree" className={cls('tree')}>
        <rect x="150" y="410" width="26" height="90" rx="8" fill={p.trunk} />
        <circle cx="163" cy="380" r="58" fill={p.tree} />
        <circle cx="120" cy="405" r="40" fill={p.tree} />
        <circle cx="205" cy="405" r="40" fill={p.tree} />
      </g>

      {/* ── Pieza: cuerpo de la casa ── */}
      <g data-piece="house-body" className={cls('house-body')}>
        <rect x="360" y="360" width="230" height="160" rx="6" fill={p.houseWall} />
      </g>

      {/* ── Pieza: techo ── */}
      <g data-piece="house-roof" className={cls('house-roof')}>
        <path d="M340 365 L475 275 L610 365 Z" fill={p.houseRoof} />
      </g>

      {/* ── Pieza: puerta y ventana ── */}
      <g data-piece="door-window" className={cls('door-window')}>
        <rect x="415" y="430" width="55" height="90" rx="6" fill={p.houseDoor} />
        <circle cx="458" cy="478" r="4" fill={p.houseWindow} />
        <rect x="500" y="400" width="60" height="55" rx="6" fill={p.houseWindow} />
        <line x1="530" y1="400" x2="530" y2="455" stroke={p.houseWall} strokeWidth="4" />
        <line x1="500" y1="427" x2="560" y2="427" stroke={p.houseWall} strokeWidth="4" />
      </g>

      {/* ── Pieza: pájaros (Galia es un pájaro) ── */}
      <g data-piece="birds" className={cls('birds')} fill="none" stroke={p.bird} strokeWidth="4" strokeLinecap="round">
        <path d="M250 150 Q 268 132 286 150" />
        <path d="M286 150 Q 304 132 322 150" />
        <path d="M330 190 Q 344 176 358 190" />
        <path d="M358 190 Q 372 176 386 190" />
      </g>

      {/* ── Pieza: jardín (completa la casa de Galia) ── */}
      <g data-piece="garden" className={cls('garden')}>
        {GARDEN_FLOWERS.map((x, i) => (
          <g key={i} transform={`translate(${x} 508)`}>
            <rect x="-2" y="0" width="4" height="24" fill={p.tree} />
            <circle cx="0" cy="-4" r="9" fill={p.flower} />
            <circle cx="0" cy="-4" r="3.5" fill={p.celestial} />
          </g>
        ))}
      </g>
    </svg>
  );
}

// Posiciones de las flores del jardín (eje X en el viewBox).
const GARDEN_FLOWERS = [355, 400, 448, 500, 552, 600];

// Estrellas para la escena nocturna (B2): [cx, cy, r, opacity].
const STARS: Array<[number, number, number, number]> = [
  [80, 80, 2, 0.9], [160, 140, 1.5, 0.7], [240, 60, 2.5, 1], [330, 110, 1.8, 0.8],
  [430, 70, 2, 0.9], [520, 130, 1.5, 0.6], [710, 90, 2.2, 1], [760, 200, 1.6, 0.7],
  [40, 200, 1.8, 0.8], [120, 260, 1.4, 0.6], [200, 210, 2, 0.9], [400, 180, 1.5, 0.7],
];

export default GaliaScene;
